import { useContext, useState, useEffect, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { FastField, Form, Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import { RequestContext } from 'bxcommon/context/Request.context';
import { RequestStepWrapper } from 'bxcommon/components/RequestStepWrapper/RequestStepWrapper';
import { FormWrapper } from 'bxcommon/components/FormWrapper/FormWrapper';
import AppConfig from 'bxcommon/providers/AppConfig';
import {
  createCleanFields,
  useValidation,
  requiredFilesValidator,
  createUploadBox,
} from 'bxcommon/helpers/form.helper';
import { BaseBox, BaseBoxHeading, BaseDatePicker, BaseHint, InputField, CheckboxField } from 'bxcommon';

import config from '../../prospectus.config';

import './FormProspectus.scss';

const { maxFileSize } = config;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('Form error caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.state.error);
    }
    return this.props.children;
  }
}

export const FormProspectus = (props) => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const { request, updateRequestField, formRef, FILE_UPLOAD_TYPES } = useContext(RequestContext);

  const [footerDisabled, disableFooter] = useState(false);
  const renderUploadBox = createUploadBox(disableFooter);

  const [validationSchema, submitWithValidation] = useValidation(
    Yup.object().shape(
      {
        publish_on: Yup.object()
          .nullable()
          .when('publish_here', {
            is: true,
            then: () => Yup.object().required(common('errors.requiredField')),
            otherwise: () => Yup.object().notRequired(),
          }),
        already_published_location: Yup.string().when('is_already_published', {
          is: true,
          then: () => Yup.string().required(common('errors.requiredField')),
          otherwise: () => Yup.string().notRequired(),
        }),
        already_published_date: Yup.object()
          .nullable()
          .when('is_already_published', {
            is: true,
            then: () => Yup.object().required(common('errors.requiredField')),
            otherwise: () => Yup.object().notRequired(),
          }),
        prospectus_files: requiredFilesValidator(),
      },
      [['is_already_published', 'publish_here']]
    )
  );

  const initialFormValues = {
    publish_here: request.publish_here || false,
    publish_on: request.publish_on ? dayjs(request.publish_on) : null,
    is_already_published: Boolean(request.already_published_location || request.already_published_date),
    paper_version: request.paper_version || '',
    already_published_location: request.already_published_location || '',
    already_published_date: request.already_published_date ? dayjs(request.already_published_date) : null,
    prospectus_files: request.prospectus_files || [],
    incorporated_files: request.incorporated_files || [],
    additional_files: request.additional_files || [],
  };

  const cleanFields = createCleanFields({
    publish_here: [{ key: 'publish_on', value: null }],
    is_already_published: [
      { key: 'already_published_location', value: '' },
      { key: 'already_published_date', value: null },
    ],
  });

  const createDatePicker = (name, label, disabled = false, disabledDate = null) => (
    <FastField
      component={BaseDatePicker}
      name={name}
      label={label}
      disabled={disabled}
      disabledDate={disabledDate}
      format={AppConfig.defaultDateFormat}
      data-testid={name}
    />
  );

  const onFormSubmit = async () => {
    const values = formRef.current.values;
    values.security_type = request.security_type;
    updateRequestField({ ...values });
  };

  useEffect(() => {
    console.log('FormProspectus context/props updated:', {
      hasRequest: !!request,
      hasFormRef: !!formRef,
      props,
    });
  }, [request, formRef, props]);

  const onFormikError = (error) => {
    console.error('FormProspectus Formik error:', error);
    return (
      <div style={{ padding: '20px', border: '1px solid red' }}>
        <h3>Form error occurred</h3>
        <p>Please refresh the page or contact support.</p>
        <pre>{String(error)}</pre>
      </div>
    );
  };

  if (!request || !formRef) {
    // oxlint-disable-next-line react-hooks/refs
    console.warn('FormProspectus missing required context:', { request, formRef });
    return (
      <FormWrapper>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <div>Loading prospectus form data...</div>
        </div>
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <RequestStepWrapper
        formRef={formRef}
        validationSubmit={submitWithValidation}
        disabled={footerDisabled}
        {...props}
      >
        <ErrorBoundary fallback={onFormikError}>
          <Formik
            innerRef={formRef}
            initialValues={initialFormValues}
            onSubmit={() => onFormSubmit()}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({ values, setFieldValue }) => (
              <>
                <BaseBox className="request-form__box" lifted>
                  <BaseBoxHeading>{t('request.form.prospectus.basicInformation.place_of_publication')}</BaseBoxHeading>
                  <Form className="form-container">
                    <div className="request-form--with-close-hint">
                      <CheckboxField
                        name="publish_here"
                        checked={values.publish_here}
                        text={t('request.form.prospectus.basicInformation.publish_here')}
                        onChange={(value, fieldName) => cleanFields(value, fieldName, setFieldValue)}
                      />
                      <div className="row">
                        <div className="col-5 request__form-input-offset">
                          {createDatePicker(
                            'publish_on',
                            t('request.form.prospectus.basicInformation.publish_on'),
                            !values.publish_here,
                            (v) => v < dayjs().subtract(1, 'day')
                          )}
                        </div>
                      </div>
                      <BaseHint>{t('hints.publishHere')}</BaseHint>
                    </div>
                    <div className="request-form--with-close-hint">
                      <div className="divider divider--dashed" />
                      <CheckboxField
                        name="is_already_published"
                        checked={values.is_already_published}
                        text={t('request.form.prospectus.basicInformation.is_already_published')}
                        onChange={(value, fieldName) => cleanFields(value, fieldName, setFieldValue)}
                      />
                      <div className="request__form-input-offset">
                        <InputField
                          name="already_published_location"
                          label={null}
                          disabled={!values.is_already_published}
                        />
                      </div>
                      <div className="row">
                        <div className="col-5 request__form-input-offset">
                          {createDatePicker(
                            'already_published_date',
                            t('request.form.prospectus.basicInformation.already_published_date'),
                            !values.is_already_published
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <InputField
                        name="paper_version"
                        label={t('request.form.prospectus.basicInformation.paper_version')}
                      />
                    </div>
                  </Form>
                </BaseBox>
                <BaseBox className="request-form__box" lifted>
                  <BaseBoxHeading>{t('request.form.prospectus.typeOfProspectus.heading')}</BaseBoxHeading>
                  <Form className="form-container">
                    {renderUploadBox({
                      name: 'prospectus_files',
                      title: t('request.form.prospectus.typeOfProspectus.uploadProspectus'),
                      conditions: common('request.form.common.fileDefaultsPDF'),
                      type: FILE_UPLOAD_TYPES.PROSPECTUS_FILE,
                      maxFileSize,
                      config: {
                        multiple: true,
                      },
                    })}

                    <div className="request-form--with-close-hint">
                      {renderUploadBox({
                        name: 'incorporated_files',
                        title: t('request.form.prospectus.typeOfProspectus.uploadIncorporated'),
                        conditions: common('request.form.common.fileDefaultsPDF'),
                        type: FILE_UPLOAD_TYPES.INCORPORATED_FILE,
                        maxFileSize,
                        config: {
                          multiple: true,
                        },
                      })}
                      <BaseHint>{t('request.form.prospectus.typeOfProspectus.uploadIncorporatedHint')}</BaseHint>
                    </div>

                    {renderUploadBox({
                      name: 'additional_files',
                      title: `
                      ${t('request.form.prospectus.typeOfProspectus.uploadAdditionalFiles')}
                      ${common('request.form.common.optional')}
                    `,
                      conditions: common('request.form.common.fileDefaultsWordPngJpg'),
                      type: FILE_UPLOAD_TYPES.ADDITIONAL_FILE,
                      maxFileSize,
                      config: {
                        multiple: true,
                        pdfOnly: false,
                      },
                      otherProps: { basic: true },
                    })}
                  </Form>
                </BaseBox>
              </>
            )}
          </Formik>
        </ErrorBoundary>
      </RequestStepWrapper>
    </FormWrapper>
  );
};

export default FormProspectus;
